<template>
  <div class="search-form-container">
    <form class="sanitasForms" @submit.prevent="inputSearch">
      <TextInput
        id="searchBox"
        ref="searchBox"
        v-model="searchValue"
        class="search__form__input-box"
        autocomplete="off"
        :placeholder="$t('input-placeholder')"
        title-label=""
        autofocus
        :max-length="100"
        @input="onInputDebouncedSearch"
      />
    </form>

    <span id="search-icon-box">
      <dotcom-icon
        v-show="!searchHasEnoughChars"
        name="magnifying-glass"
        art="light"
        color="--c-primary-neutral-1"
        hover-color="--c-primary-color-3"
        @click="inputSearch"
      />
      <dotcom-icon
        v-show="searchHasEnoughChars"
        name="xmark"
        art="light"
        color="--c-primary-neutral-1"
        hover-color="--c-primary-color-3"
        @click="userDidPressClear"
      />
    </span>

    <div v-if="showMinLengthMessage" class="search-form__min-length-message">
      {{ $t('min-three-chars') }}
    </div>

    <div v-if="showStaticSuggestions" class="search-form__static-suggestions">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import { useI36n } from '@jota-one/i36n/src/index.vue2'
import debounce from 'lodash/debounce'

export default {
  name: 'SanitasFormSearch',
  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
    TextInput: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/TextInput'
      )
    },
  },

  props: {
    autocompleteSearchEndpointUrl: {
      type: String,
      default: '',
    },

    minimumSearchLength: {
      type: Number,
      default: 3,
    },

    maxVisibleSuggestions: {
      type: Number,
      default: 10,
    },

    comboboxData: {
      type: Array,
      default: () => [],
    },

    shouldShowResults: Boolean,
    showLoading: Boolean,
    searchTerm: {
      type: String,
      default: '',
    },
  },

  setup() {
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },

  data: () => ({
    searchValue: '',
    showMinLengthMessage: false,
    preventFastSecondCall: false,
  }),

  computed: {
    showStaticSuggestions() {
      return (
        !this.shouldShowResults &&
        (this.searchValue === undefined || this.searchValue?.length < 3)
      )
    },
    searchHasEnoughChars() {
      return this.searchValue?.length >= this.minimumSearchLength
    },
  },
  watch: {
    searchTerm(newSearchTerm) {
      this.searchValue = newSearchTerm
      this.inputSearch()
    },
  },

  created() {
    this.onInputDebouncedSearch = debounce(this.inputSearch, 500)
  },
  mounted() {
    this.focusInput()

    if (this.searchTerm) {
      this.searchValue = this.searchTerm
      this.inputSearch()
    }
  },

  methods: {
    inputSearch() {
      if (this.showLoading) {
        return
      }
      if (this.preventFastSecondCall) {
        return
      }

      this.preventFastSecondCall = true
      setTimeout(this.preventFastSecondCallFunction, 500)

      if (this.searchValue === undefined) {
        return
      }

      this.showMinLengthMessage =
        this.searchValue.length < this.minimumSearchLength

      this.userDidPressSearch(this.searchValue)
      if (this.searchValue === undefined || this.searchValue === '') {
        this.focusInput()
      }
    },
    preventFastSecondCallFunction() {
      this.preventFastSecondCall = false
    },
    userDidPressSearch(value) {
      this.$emit('submit', { value })
    },
    userDidPressClear() {
      this.searchValue = ''
      this.userDidPressSearch(this.searchValue)
      this.focusInput()
    },
    focusInput() {
      setTimeout(() => {
        this.$refs.searchBox.focus()
      }, 100)
    },
  },
}
</script>

<style lang="postcss">
.search-form-container {
  position: relative;

  .search__form__input-box {
    display: block;
    margin-bottom: var(--spacing-2);
    padding-right: 0;

    label {
      .label {
        .title {
          color: black !important;
          font-size: 24px !important;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          width: 100%;
          @media (--xl) {
            font-size: 36px !important;
            top: 17px !important;
          }
        }

        .bar {
          display: none !important;
        }
      }

      input {
        font-weight: 400 !important;
        font-style: normal !important;
        font-size: 24px !important;
        line-height: 1.25 !important;
        color: var(--c-primary-neutral-1) !important;
        border-color: black !important;
        @media (--xl) {
          font-size: 24px !important;
        }

        &:focus:not([readonly]) ~ .label .title {
          top: 0 !important;
          font-size: 12px !important;
        }
      }

      &.touched .label .title {
        top: 0 !important;
        font-size: 12px !important;
      }
    }
  }

  #search-icon-box {
    position: absolute;
    top: 14px;
    right: 0;
    cursor: pointer;
    z-index: 99;

    .dotcom-icon {
      width: 30px;
      height: 30px;
      padding: 6px;
      box-sizing: border-box;
    }

    @media (--xl) {
      .dotcom-icon {
        width: 32px;
        height: 32px;
      }
    }
  }
}

.search-form__static-suggestions {
  margin-top: 56px;
}

.search-form__min-length-message {
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.2px;
  margin-top: -14px;
  margin-left: 11px;
}
</style>
